import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axiosInstance from "../services/axiosInterceptors";
import apiEndPointsConfig from "../services/apiEndPoints";

const PermissionsContext = createContext();

// Fetched once per Layout mount (i.e. on login / hard reload — not on every client-side
// route change) so a permission edit made via the Roles & Permissions screen takes effect
// on this user's next page load, without forcing a re-login.
export const PermissionsProvider = ({ children }) => {
  const [pages, setPages] = useState(null);
  const [dataScope, setDataScope] = useState("full");
  // Role-based Phone Number Masking — read through usePhoneMasking (utils/phoneMasking.js).
  const [phoneNumberMasking, setPhoneNumberMasking] = useState(true);
  const [loading, setLoading] = useState(true);

  const fetchPermissions = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setPages(null);
      setLoading(false);
      return;
    }
    try {
      const res = await axiosInstance.get(apiEndPointsConfig.myPermissions, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = res?.data?.data;
      setPages(data?.pages || null);
      setDataScope(data?.dataScope || "full");
      setPhoneNumberMasking(Boolean(data?.phoneNumberMasking));
    } catch (err) {
      console.error("Failed to fetch permissions:", err);
      // Fail closed on the frontend too — an empty pages object means every
      // requiredPage check below denies access rather than silently allowing it.
      setPages({});
      setPhoneNumberMasking(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  return (
    <PermissionsContext.Provider value={{ pages, dataScope, phoneNumberMasking, loading, refetchPermissions: fetchPermissions }}>
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = () => useContext(PermissionsContext);
